import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { day, inr, num } from '../lib/format.js';
import { Alert, Card, CardSkeleton, Empty, LoadMore, Pill, usePagedList } from '../components/ui.jsx';
import PeriodPicker, { useUrlPeriod } from '../components/PeriodPicker.jsx';
import ExportButton, { fetchAllPages, periodSlug, periodText } from '../components/ExportButton.jsx';
import { useEntityQuery } from '../lib/entity.js';

export default function Invoices() {
  const [params, setParams] = useSearchParams();
  const syncStatus = params.get('sync_status') || '';
  // A link asking after Tally status wants every stuck invoice, not this month's.
  const { period, setPeriod } = useUrlPeriod({ allByDefault: !!syncStatus });
  const status = params.get('status') || '';
  const navigate = useNavigate();

  const entityQuery = useEntityQuery();
  const query = new URLSearchParams(entityQuery);
  if (period) { query.set('from', period.from); query.set('to', period.to); }
  if (syncStatus) query.set('sync_status', syncStatus);
  if (status) query.set('status', status);
  // A fresh filter starts a fresh list; a page still in flight for the old one is dropped.
  const list = usePagedList(`/api/invoices?${query.toString()}`, 'invoices');
  const cursor = list.hasMore;

  const setFilter = (patch) => {
    // The period is carried explicitly, so clearing a filter does not change it.
    const next = {
      ...(period ? { from: period.from, to: period.to } : { range: 'all' }),
      ...(syncStatus ? { sync_status: syncStatus } : {}),
      ...(status ? { status } : {}),
      ...patch,
    };
    Object.keys(next).forEach((k) => !next[k] && delete next[k]);
    setParams(next);
  };

  const rows = list.data ? list.data.invoices : [];
  const totals = list.data ? rows.reduce((a, i) => ({
    taxable: a.taxable + num(i.taxable_value), total: a.total + num(i.grand_total),
  }), { taxable: 0, total: 0 }) : null;

  return (
    <>
      <div className="page-head">
        <div>
          <h1 className="page-title">Tax invoices</h1>
          <div className="page-sub">
            Document 3 of the sale. Issued from a confirmed despatch, with the batch properties frozen onto it,
            then posted to Tally by the sync agent.
          </div>
        </div>
        <div className="inline">
          <PeriodPicker allowAll value={period} onChange={setPeriod} />
          <ExportButton build={async () => ({
            filename: `invoices-${periodSlug(period)}`,
            title: 'Tax invoices',
            subtitle: periodText(period),
            sheets: [{
              name: 'Invoices',
              columns: [
                { key: 'invoice_number', header: 'Invoice' }, { key: 'invoice_date', header: 'Date', type: 'date' },
                { key: 'buyer_name', header: 'Buyer' }, { key: 'buyer_gstin', header: 'GSTIN' },
                { key: 'supply', header: 'Supply' }, { key: 'godown_name', header: 'Godown' },
                { key: 'taxable_value', header: 'Taxable', type: 'money', total: true },
                { key: 'grand_total', header: 'Total', type: 'money', total: true },
                { key: 'status', header: 'Status' }, { key: 'tally_sync_status', header: 'Tally' },
                { key: 'tally_voucher_no', header: 'Tally voucher' },
              ],
              rows: (await fetchAllPages(`/api/invoices?${query.toString()}`, 'invoices')).map((i) => ({
                ...i, supply: i.is_interstate ? 'Inter-state' : 'Intra-state',
              })),
            }],
          })} />
        </div>
      </div>

      {(syncStatus || status) && (
        <div className="filter-chips">
          {status && (
            <span className="chip">
              Status: {status.replace(/,/g, ', ').replace(/_/g, ' ')}
              <button aria-label="Clear status filter" onClick={() => setFilter({ status: '' })}>×</button>
            </span>
          )}
          {syncStatus && (
            <span className="chip">
              Tally status: {syncStatus.replace(/,/g, ', ')}
              <button aria-label="Clear filter" onClick={() => setFilter({ sync_status: '' })}>×</button>
            </span>
          )}
        </div>
      )}

      <Alert onRetry={list.reload}>{list.error}</Alert>
      {list.loading && !list.data && <CardSkeleton lines={6} />}

      {list.data && (
        <Card pad={rows.length === 0}>
          {rows.length === 0 ? (
            <Empty title="No invoices in this view">
              Invoices are raised from a confirmed despatch under Despatches.
            </Empty>
          ) : (
            <div className="table-wrap" style={{ margin: 0, padding: 0 }}>
              <table>
                <thead>
                  <tr><th>Invoice</th><th>Buyer</th><th>Supply</th>
                    <th className="r">Taxable</th><th className="r">Total</th><th>Status</th><th>Tally</th></tr>
                </thead>
                <tbody>
                  {rows.map((i) => (
                    <tr key={i.id} className="clickable" onClick={() => navigate(`/invoices/${i.id}`)}>
                      <td className="mono">{i.invoice_number || 'draft'}<div className="sub">{day(i.invoice_date)}</div></td>
                      <td>{i.buyer_name}<div className="sub mono">{i.buyer_gstin || 'Unregistered'}</div></td>
                      <td>{i.is_interstate ? 'Inter-state' : 'Intra-state'}
                        <div className="sub">{i.is_interstate ? 'IGST' : 'CGST + SGST'}</div></td>
                      <td className="r num">{inr(i.taxable_value, 0)}</td>
                      <td className="r num"><b>{inr(i.grand_total, 0)}</b></td>
                      <td><Pill value={i.status} /></td>
                      <td><Pill value={i.tally_sync_status} />
                        {i.tally_voucher_no && <div className="sub">voucher #{i.tally_voucher_no}</div>}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={3} className="r">
                      {cursor ? `Subtotal of ${rows.length} shown` : `Total of ${rows.length} invoices`}
                      {cursor && <div className="sub">more rows remain — load them for the full total</div>}
                    </th>
                    <th className="r num">{inr(totals.taxable, 0)}</th>
                    <th className="r num">{inr(totals.total, 0)}</th>
                    <th colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
          <LoadMore list={list} />
        </Card>
      )}
    </>
  );
}
